<script setup lang="ts">
    type MoodImage = {
        filename?: string;
        alt?: string;
    };

    const {
        title = '',
        sidebarTitle,
        sidebarButtonOpenText,
        sidebarButtonClosedText,
        moodImage = {},
        filterCount = 0,
        totalFoundEntries = 0,
        showCta = true
    } = defineProps<{
        title?: string;
        sidebarTitle: string;
        sidebarButtonOpenText: string;
        sidebarButtonClosedText: string;
        moodImage?: MoodImage;
        filterCount?: number;
        totalFoundEntries?: number;
        showCta?: boolean;
    }>();

    const emit = defineEmits(['resetFilters']);

    const { t } = useI18n();

    const showSidebar = ref(false);
    const route = useRoute();

    const selectedCategory = computed(() => route.query.category || null);

    const isDesktop = ref(false);

    const checkViewportSize = () => {
        isDesktop.value = window.innerWidth > 1024;

        if (!isDesktop.value && showSidebar.value) {
            document.body.style.overflow = 'hidden';
        }
    };

    const toggleSidebar = () => {
        showSidebar.value = !showSidebar.value;
        document.body.style.overflow = showSidebar.value && !isDesktop.value ? 'hidden' : '';
    };

    watch(
        () => selectedCategory.value,
        (newCategory) => {
            if (newCategory && showSidebar) {
                toggleSidebar();
            }
        }
    );

    onMounted(() => {
        checkViewportSize();
        window.addEventListener('resize', checkViewportSize);
    });

    onUnmounted(() => {
        window.removeEventListener('resize', checkViewportSize);
    });
</script>

<template>
    <h2
        v-if="title"
        class="main-title"
        :class="{ '--with-mood-image': moodImage }"
    >
        {{ title }}
    </h2>

    <div
        v-if="moodImage.filename"
        class="mood-image"
        :class="{ '--with-title': title }"
    >
        <StoryblokImage
            :media="moodImage"
            type="mainPicture"
            :alt="moodImage.alt"
        />
    </div>

    <div
        class="grid"
        :class="{
            '--sidebar-open': showSidebar,
            '--with-only-title': !moodImage.filename && title
        }"
    >
        <div
            class="sidebar"
            :class="{ '--collapsed': !showSidebar, '--with-only-title': !moodImage.filename && title }"
        >
            <div class="header">
                <h2 class="title">{{ sidebarTitle }}</h2>

                <SharedCloseButton
                    class="close-btn"
                    :label="sidebarButtonClosedText"
                    @click="toggleSidebar"
                />
            </div>

            <div class="sidebar-content">
                <slot name="sidebar"></slot>
            </div>

            <div
                v-if="showCta"
                class="found-entries"
            >
                <PrimaryButton
                    :button-text="t('pagePlp.sidebar.foundEntries', { n: totalFoundEntries })"
                    product-button
                    has-icon
                    @click="toggleSidebar"
                />
            </div>
        </div>

        <div class="content-wrapper">
            <div class="user-actions">
                <div class="filter">
                    <div class="filter-btn-wrapper">
                        <PrimaryButton
                            :button-text="!showSidebar ? sidebarButtonOpenText : sidebarButtonClosedText"
                            product-button
                            has-icon
                            variant="secondary"
                            class="sidebar-btn"
                            @click="toggleSidebar"
                        >
                            <template #customIcon>
                                <img
                                    v-if="!showSidebar"
                                    class="icon"
                                    src="~/assets/icons/panel-right-close.svg"
                                    alt=""
                                    aria-hidden="true"
                                />

                                <img
                                    v-else
                                    class="icon"
                                    src="~/assets/icons/panel-right-open.svg"
                                    alt=""
                                    aria-hidden="true"
                                />
                            </template>
                        </PrimaryButton>

                        <span
                            v-if="filterCount >= 1"
                            class="fade-in"
                            aria-hidden="true"
                        >
                            {{ filterCount }}
                        </span>
                    </div>

                    <button
                        v-if="filterCount >= 1"
                        class="filter-reset-button fade-in"
                        @click="emit('resetFilters')"
                    >
                        <span>{{ t('pagePlp.sidebar.resetFilters') }}</span>
                        <img
                            class="icon"
                            src="~/assets/icons/close-blue.svg"
                            alt="close"
                            aria-hidden="true"
                        />
                    </button>
                </div>

                <div class="custom-user-actions">
                    <slot name="user-actions"></slot>
                </div>
            </div>

            <slot name="filter-chips"></slot>

            <div class="content">
                <slot name="content"></slot>
            </div>
        </div>
    </div>
</template>

<style scoped>
    @import '~/assets/scss/grid/sidebar-grid.scss';
</style>
